// src/pages/admin/company/BinsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../services/firebase";

export default function BinsTab({ company }) {
  const [bins, setBins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");

  const [onHireByBin, setOnHireByBin] = useState({});

  const [editingBin, setEditingBin] = useState(null);
  const [binForm, setBinForm] = useState({
    name: "",
    volume: "",
    stockTotal: 0,
    categoryId: "",
    categoryName: "",
  });

  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
  });

  useEffect(() => {
    if (!company?.id) return;

    const qy = query(collection(db, "bins"), where("companyId", "==", company.id));
    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setBins(list);
    });
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;

    const qy = query(
      collection(db, "bin_categories"),
      where("companyId", "==", company.id)
    );

    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setCategories(list);
    });
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;

    const qy = query(
      collection(db, "bin_loans"),
      where("companyId", "==", company.id)
    );

    return onSnapshot(qy, (snap) => {
      const map = {};
      snap.forEach((d) => {
        const x = d.data();
        const bid = String(x.binId || "");
        const qty = Number(x.qty || 0);
        if (!bid || !qty) return;

        if (x.direction === "out") map[bid] = (map[bid] || 0) + qty;
        else if (x.direction === "in") map[bid] = (map[bid] || 0) - qty;
      });

      Object.keys(map).forEach((k) => {
        if (map[k] < 0) map[k] = 0;
      });

      setOnHireByBin(map);
    });
  }, [company?.id]);

  const filteredBins = useMemo(() => {
    const s = filter.trim().toLowerCase();

    if (!s) return bins;

    return bins.filter((r) => {
      const binName = String(r.name || "").toLowerCase();
      const categoryName = String(r.categoryName || "").toLowerCase();
      return binName.includes(s) || categoryName.includes(s);
    });
  }, [bins, filter]);

  const groupedBins = useMemo(() => {
    const map = new Map();

    categories.forEach((cat) => {
      map.set(cat.id, {
        id: cat.id,
        name: cat.name || "Untitled category",
        bins: [],
      });
    });

    const uncategorised = {
      id: "uncategorised",
      name: "Uncategorised",
      bins: [],
    };

    filteredBins.forEach((bin) => {
      if (bin.categoryId && map.has(bin.categoryId)) {
        map.get(bin.categoryId).bins.push(bin);
      } else {
        uncategorised.bins.push(bin);
      }
    });

    const groups = Array.from(map.values());

    if (uncategorised.bins.length > 0) {
      groups.push(uncategorised);
    }

    return groups;
  }, [categories, filteredBins]);

  function openNewBin() {
    setEditingBin({});
    setBinForm({
      name: "",
      volume: "",
      stockTotal: 0,
      categoryId: "",
      categoryName: "",
    });
  }

  function openEditBin(r) {
    setEditingBin(r);
    setBinForm({
      name: r.name || "",
      volume: r.volume ?? "",
      stockTotal: Number(r.stockTotal || 0),
      categoryId: r.categoryId || "",
      categoryName: r.categoryName || "",
    });
  }

  function closeBinModal() {
    setEditingBin(null);
  }

  function openNewCategory() {
    setEditingCategory({});
    setCategoryForm({ name: "" });
  }

  function openEditCategory(category) {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || "",
    });
  }

  function closeCategoryModal() {
    setEditingCategory(null);
  }

  async function saveBin() {
    const selectedCategory =
      categories.find((c) => c.id === binForm.categoryId) || null;

    const payload = {
      companyId: company.id,
      companyName: company.name,
      name: String(binForm.name || "").trim(),
      volume: Number(binForm.volume || 0),
      stockTotal: Number(binForm.stockTotal || 0),
      categoryId: selectedCategory?.id || "",
      categoryName: selectedCategory?.name || "",
      updatedAt: serverTimestamp(),
    };

    if (!payload.name) {
      alert("Please enter a bin name.");
      return;
    }

    if (!editingBin?.id) {
      await addDoc(collection(db, "bins"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "bins", editingBin.id), payload);
    }

    setEditingBin(null);
  }

  async function saveCategory() {
    const name = String(categoryForm.name || "").trim();

    if (!name) {
      alert("Please enter a category name.");
      return;
    }

    const duplicate = categories.find(
      (c) =>
        c.id !== editingCategory?.id &&
        String(c.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      alert("A category with this name already exists.");
      return;
    }

    const payload = {
      companyId: company.id,
      companyName: company.name,
      name,
      updatedAt: serverTimestamp(),
    };

    if (!editingCategory?.id) {
      await addDoc(collection(db, "bin_categories"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "bin_categories", editingCategory.id), payload);

      const affectedBins = bins.filter((b) => b.categoryId === editingCategory.id);
      await Promise.all(
        affectedBins.map((b) =>
          updateDoc(doc(db, "bins", b.id), {
            categoryName: name,
            updatedAt: serverTimestamp(),
          })
        )
      );
    }

    setEditingCategory(null);
  }

  async function removeBin(id) {
    if (!window.confirm("Delete this bin?")) return;
    await deleteDoc(doc(db, "bins", id));
  }

  async function removeCategory(category) {
    const usedByBins = bins.filter((b) => b.categoryId === category.id);

    if (usedByBins.length > 0) {
      const ok = window.confirm(
        `This category has ${usedByBins.length} bin(s). Deleting it will move those bins to Uncategorised. Continue?`
      );
      if (!ok) return;

      await Promise.all(
        usedByBins.map((b) =>
          updateDoc(doc(db, "bins", b.id), {
            categoryId: "",
            categoryName: "",
            updatedAt: serverTimestamp(),
          })
        )
      );
    } else {
      const ok = window.confirm("Delete this category?");
      if (!ok) return;
    }

    await deleteDoc(doc(db, "bin_categories", category.id));
  }

  function renderBinTable(rows) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50/90">
            <tr>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Name / Size
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Volume (L)
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Stock
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                On hire
              </th>
              <th className="p-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Available
              </th>
              <th className="p-4" />
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => {
              const stock = Number(r.stockTotal || 0);
              const onHire = Math.max(0, Number(onHireByBin[r.id] || 0));
              const available = stock - onHire;
              const lowStock = available <= 0;

              return (
                <tr
                  key={r.id}
                  className="border-t border-gray-100 align-top transition hover:bg-gray-50/40"
                >
                  <td className="p-4">
                    <div className="font-semibold text-gray-900">{r.name}</div>
                  </td>

                  <td className="p-4 text-sm text-gray-700 tabular-nums">
                    <span className="inline-flex rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1">
                      {Number(r.volume || 0)}
                    </span>
                  </td>

                  <td className="p-4 text-sm tabular-nums text-gray-800">
                    <span className="inline-flex min-w-[2.5rem] justify-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1">
                      {stock}
                    </span>
                  </td>

                  <td className="p-4 text-sm tabular-nums text-gray-800">
                    <span className="inline-flex min-w-[2.5rem] justify-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-800">
                      {onHire}
                    </span>
                  </td>

                  <td className="p-4 text-sm tabular-nums">
                    <span
                      className={`inline-flex min-w-[2.75rem] justify-center rounded-lg px-2.5 py-1 font-medium border ${
                        lowStock
                          ? "border-red-200 bg-red-50 text-red-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {available}
                    </span>
                  </td>

                  <td className="p-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 hover:border-gray-400"
                        onClick={() => openEditBin(r)}
                      >
                        Edit
                      </button>
                      <button
                        className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-red-600 shadow-sm transition hover:bg-red-50 hover:border-red-300"
                        onClick={() => removeBin(r.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {rows.length === 0 && (
              <tr>
                <td className="p-6 text-sm text-gray-500" colSpan={6}>
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center">
                    No bins in this category.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="w-full sm:w-72 rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          placeholder="Search bins or categories"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <button
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-100"
            onClick={openNewCategory}
          >
            New category
          </button>
          <button
            className="rounded-xl border border-gray-900 bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-black"
            onClick={openNewBin}
          >
            New bin
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Categories</h3>
            <p className="text-xs text-gray-500">
              Create categories and place bins inside each one.
            </p>
          </div>
        </div>

        {categories.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-500">
            No categories yet. Create your first category to organise bins.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const count = bins.filter((b) => b.categoryId === category.id).length;

              return (
                <div
                  key={category.id}
                  className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {category.name}
                    </div>
                    <div className="text-xs text-gray-500">{count} bin(s)</div>
                  </div>

                  <div className="ml-1 flex items-center gap-1">
                    <button
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                      onClick={() => openEditCategory(category)}
                    >
                      Edit
                    </button>
                    <button
                      className="rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50"
                      onClick={() => removeCategory(category)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {groupedBins.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
            No bins found.
          </div>
        ) : (
          groupedBins.map((group) => (
            <div
              key={group.id}
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{group.name}</h3>
                  <p className="text-xs text-gray-500">
                    {group.bins.length} bin(s) in this category
                  </p>
                </div>

                {group.id !== "uncategorised" && (
                  <button
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                    onClick={() =>
                      openEditCategory(categories.find((c) => c.id === group.id))
                    }
                  >
                    Edit category
                  </button>
                )}
              </div>

              {renderBinTable(group.bins)}
            </div>
          ))
        )}
      </div>

      {editingBin && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={closeBinModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingBin.id ? "Edit bin" : "New bin"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Add a bin and assign it to a category.
              </p>
            </div>

            <div className="grid gap-4 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Category
                </label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={binForm.categoryId}
                  onChange={(e) => {
                    const categoryId = e.target.value;
                    const category = categories.find((c) => c.id === categoryId);
                    setBinForm((p) => ({
                      ...p,
                      categoryId,
                      categoryName: category?.name || "",
                    }));
                  }}
                >
                  <option value="">Uncategorised</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Name
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Name"
                  value={binForm.name}
                  onChange={(e) =>
                    setBinForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Volume (L)
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  type="number"
                  placeholder="Volume (L)"
                  value={binForm.volume}
                  onChange={(e) =>
                    setBinForm((p) => ({ ...p, volume: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Stock (owned)
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  type="number"
                  placeholder="Stock (owned)"
                  value={binForm.stockTotal}
                  onChange={(e) =>
                    setBinForm((p) => ({ ...p, stockTotal: e.target.value }))
                  }
                />
              </div>

              <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-blue-900">
                On hire is calculated from deliveries and pickups and will never
                be negative.
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
              <button
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                onClick={closeBinModal}
              >
                Cancel
              </button>
              <button
                className="rounded-xl border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black"
                onClick={saveBin}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCategory && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={closeCategoryModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCategory.id ? "Edit category" : "New category"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create a category to organise your bins.
              </p>
            </div>

            <div className="grid gap-4 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Category name
                </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g. Hook bins, Marrell bins, Skip bins"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
              <button
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                onClick={closeCategoryModal}
              >
                Cancel
              </button>
              <button
                className="rounded-xl border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black"
                onClick={saveCategory}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}